import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import ViolationCategoriesSection from './components/ViolationCategoriesSection';
import PointThresholdsSection from './components/PointThresholdsSection';
import ComplianceRulesSection from './components/ComplianceRulesSection';
import DepartmentSettingsSection from './components/DepartmentSettingsSection';
import RoleManagementSection from './components/RoleManagementSection';
import JobFieldsConfigSection from './components/JobFieldsConfigSection';
import SystemConfigurationSection from './components/SystemConfigurationSection';
import DataResetSection from './components/DataResetSection';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('violations');

  const tabs = [
    {
      id: 'violations',
      label: 'Violation Categories',
      icon: 'AlertTriangle',
      description: 'Manage violation types, severity levels, and default point values'
    },
    {
      id: 'thresholds',
      label: 'Point Thresholds',
      icon: 'TrendingUp',
      description: 'Configure warning levels and intervention triggers'
    },
    {
      id: 'rules',
      label: 'Compliance Rules',
      icon: 'public/assets/images/Real_logo-1771587686730.png',
      description: 'Create conditional logic and automated workflows'
    },
    {
      id: 'departments',
      label: 'Department Settings',
      icon: 'Building',
      description: 'Per-department customization and approval workflows'
    },
    {
      id: 'roles',
      label: 'Role Management',
      icon: 'Users',
      description: 'Define roles and assign permissions'
    },
    {
      id: 'jobfields',
      label: 'Job Fields Config',
      icon: 'Briefcase',
      description: 'Customize job detail fields and configurations'
    },
    {
      id: 'system',
      label: 'System Configuration',
      icon: 'Settings',
      description: 'Global settings and integration endpoints'
    },
    {
      id: 'pointcredits',
      label: 'Point Credit System',
      icon: 'Award',
      description: 'Automatic 1-point credit after 30 days without violations'
    },
    {
      id: 'datareset',
      label: 'Data Reset',
      icon: 'Trash2',
      description: 'Clear all employee and violation data to start fresh'
    }
  ];

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Settings', path: '/settings' }
  ];

  const renderActiveSection = () => {
    switch (activeTab) {
      case 'violations':
        return <ViolationCategoriesSection />;
      case 'thresholds':
        return <PointThresholdsSection />;
      case 'rules':
        return <ComplianceRulesSection />;
      case 'departments':
        return <DepartmentSettingsSection />;
      case 'roles':
        return <RoleManagementSection />;
      case 'jobfields':
        return <JobFieldsConfigSection />;
      case 'system':
        return <SystemConfigurationSection />;
      case 'pointcredits':
        return (
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-foreground mb-2">Point Credit System</h2>
                <p className="text-muted-foreground">Manage automatic point credits for employees with 30+ days without violations</p>
              </div>
              <button
                onClick={() => window.location.href = '/settings/point-credits'}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Icon name="Award" size={18} />
                Open Point Credit System
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">How It Works</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Employees automatically receive 1 point credit after 30 consecutive days without violations</li>
                    <li>• Credits reduce current points and improve compliance scores</li>
                    <li>• Only active employees with points &gt; 0 are eligible</li>
                    <li>• All credits are logged with full audit trail</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 'datareset':
        return <DataResetSection />;
      default:
        return <ViolationCategoriesSection />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="mt-4 mb-6">
            <h1 className="font-heading font-semibold text-3xl text-foreground mb-2">
              System Settings
            </h1>
            <p className="font-body text-muted-foreground">
              Configure compliance tracking rules, thresholds, and system-wide settings
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg elevation-sm p-2 space-y-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      w-full flex items-start gap-3 px-4 py-3 rounded-lg
                      transition-smooth font-body text-left
                      ${
                        activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={20} 
                      className="mt-0.5 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm">
                        {tab?.label}
                      </div>
                      <div 
                        className={`
                          text-xs mt-0.5 line-clamp-2
                          ${
                            activeTab === tab?.id
                              ? 'text-primary-foreground/80'
                              : 'text-muted-foreground'
                          }
                        `}
                      >
                        {tab?.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              <div className="bg-card rounded-lg elevation-sm">
                {renderActiveSection()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;